import Styles from './mycard.module.css';
import React, { useEffect, useState } from 'react';
import { FaInstagram, FaTiktok } from 'react-icons/fa';
import {
  ABOUT_ME_ORDER, GROWTH_LOG_CATEGORY_LABELS, GrowthLogEntry, PickupWork, STYLE_TAG_LABELS,
  StudentProfile, fetchGrowthLogs, fetchPickupWorks, fetchStudentProfile, pickupWorkImageUrl, profileImageUrl,
} from './studentProfile';
import DefaultImg from '../../../../image/default/account.png';

type Props = {
  uid: string;
  // 14.studentWorksのアカウント情報パネルはSNS欄を出さない方針のため、
  // 呼び出し側でSNSセクションを非表示にできるようにする（既定は従来どおり表示）。
  hideSns?: boolean;
  // 14.studentWorksのパネルでは、アイボリー背景＋白カードの2色構成をやめて
  // 全体を1色（パネル地色）に見せたいので、カードの背景・枠線・影を外す。
  flat?: boolean;
  // 14.studentWorksのパネルでは About Me / Pick Up Works は出さない
  // （Pick Up Works はパネル下部の「作品撮り」と重複するため）。
  hideAboutMe?: boolean;
  hidePickupWorks?: boolean;
  // 14.studentWorksは応募の有無に関わらず全学生の作品を全サロンへ見せる画面
  // なので、学校名・卒業年(schoolLine)のような個人が特定されうる情報は出さない。
  hideSchoolLine?: boolean;
};

// 学生向けアプリ「My Card」のサロン閲覧ビュー。
//
// アクセス制御(§5-3): My Card機能自体には「サロン専用の閲覧許可フラグ」がまだ無く、
// 一般公開トグル(myCard.public)しか存在しない。本パネルは「このサロンと
// 既にチャット/応募/見学のやり取りが発生している学生」だけを対象にしているため
// (=chats/{chatId}のドキュメントが既に存在する関係)、myCard.publicの値に関わらず
// 表示する方針にしている。公開設定に応じた出し分けが必要な場合は要相談。
const MyCardTab: React.FC<Props> = ({
  uid, hideSns = false, flat = false, hideAboutMe = false, hidePickupWorks = false,
  hideSchoolLine = false,
}) => {

  const [ profile, setProfile ] = useState<StudentProfile | null>(null);
  const [ growthLogs, setGrowthLogs ] = useState<GrowthLogEntry[]>([]);
  const [ pickupWorks, setPickupWorks ] = useState<PickupWork[]>([]);
  const [ loading, setLoading ] = useState(true);
  const [ aboutMeExpanded, setAboutMeExpanded ] = useState(false);

  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    (async () => {
      const p = await fetchStudentProfile(uid);
      setProfile(p);
      if (p) {
        const [ logs, works ] = await Promise.all([
          fetchGrowthLogs(uid).catch(() => []),
          fetchPickupWorks(p.myCard.pickupWorkIds).catch(() => []),
        ]);
        setGrowthLogs(logs);
        setPickupWorks(works);
      }
      setLoading(false);
    })();
  }, [uid]);

  if (loading) {
    return <p className={Styles.empty}>読み込み中...</p>;
  }

  if (!profile) {
    return <p className={Styles.empty}>My Cardの情報が見つかりませんでした。</p>;
  }

  const graduationText = profile.myCard.graduationYear
    ? `${profile.myCard.graduationYear}年${profile.myCard.graduationMonth ?? ''}月卒`
    : '';

  const filledAboutMe = ABOUT_ME_ORDER.filter(({ key }) => profile.myCard.aboutMe[key]?.trim());
  const visibleAboutMe = aboutMeExpanded ? filledAboutMe : filledAboutMe.slice(0, 1);

  const salon = profile.myCard.salonProfile;
  const hasSalonProfile = salon.preferredAreas.length > 0 || salon.desiredJobType || salon.desiredStartDate
    || salon.questionsForSalon.length > 0 || salon.messageToSalon || salon.contactMethod;

  return (
    <div className={`${Styles.wrap} ${flat ? Styles.flat : ''}`}>

      <div className={Styles.headerCard}>
        <img
          className={Styles.avatar}
          src={profileImageUrl(uid)}
          onError={(e) => {
            // 写真が無い(読み込めない)ときは「画像なし」の画像を出す(空白だと何も分からないため)
            e.currentTarget.onerror = null;
            e.currentTarget.src = DefaultImg;
          }}
          alt=""
        />
        <div>
          <h4 className={Styles.name}>{profile.displayName || '名前未設定'}</h4>
          {!hideSchoolLine && (
            <p className={Styles.schoolLine}>
              {[profile.education.school, graduationText].filter(Boolean).join(' / ')}
            </p>
          )}
          {profile.bio && <p className={Styles.bio}>{profile.bio}</p>}
          {profile.myCard.styleTagIds.length > 0 && (
            <div className={Styles.tagRow}>
              {profile.myCard.styleTagIds.slice(0, 3).map((id) => (
                <span key={id} className={Styles.tag}>{STYLE_TAG_LABELS[id] ?? id}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {!hideAboutMe && filledAboutMe.length > 0 && (
        <div className={Styles.section}>
          <h5 className={Styles.sectionHeading}>About Me</h5>
          <div className={Styles.card}>
            {visibleAboutMe.map(({ key, label }) => (
              <div key={key} className={Styles.aboutMeItem}>
                <p className={Styles.aboutMeLabel}>{label}</p>
                <p className={Styles.aboutMeText}>{profile.myCard.aboutMe[key]}</p>
              </div>
            ))}
            {filledAboutMe.length > 1 && (
              <button type="button" className={Styles.moreBtn} onClick={() => setAboutMeExpanded((v) => !v)}>
                {aboutMeExpanded ? '閉じる' : `もっと見る（他${filledAboutMe.length - 1}件）`}
              </button>
            )}
          </div>
        </div>
      )}

      {!hidePickupWorks && pickupWorks.length > 0 && (
        <div className={Styles.section}>
          <h5 className={Styles.sectionHeading}>Pick Up Works</h5>
          <div className={Styles.pickupGrid}>
            {pickupWorks.map((w) => (
              <div key={w.id} className={Styles.pickupItem}>
                <img className={Styles.pickupImage} src={pickupWorkImageUrl(w.id)} alt="" />
                {w.caption && <p className={Styles.pickupCaption}>{w.caption}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {growthLogs.length > 0 && (
        <div className={Styles.section}>
          <h5 className={Styles.sectionHeading}>Growth Log</h5>
          <div className={Styles.card}>
            <div className={Styles.timeline}>
              {growthLogs.map((log, i) => (
                <div key={log.id} className={Styles.timelineItem}>
                  <div className={Styles.timelineRail}>
                    <div className={Styles.timelineDot} />
                    {i < growthLogs.length - 1 && <div className={Styles.timelineLine} />}
                  </div>
                  <div className={Styles.timelineBody}>
                    <div className={Styles.timelineTop}>
                      <span className={Styles.timelineCategory}>{GROWTH_LOG_CATEGORY_LABELS[log.category] ?? log.category}</span>
                      <span className={Styles.timelineDate}>
                        {log.activityDate.getFullYear()}/{log.activityDate.getMonth() + 1}/{log.activityDate.getDate()}
                      </span>
                    </div>
                    <p className={Styles.timelineTitle}>{log.title}</p>
                    {log.description && <p className={Styles.timelineDesc}>{log.description}</p>}
                    {log.imageUrl && <img className={Styles.timelineImage} src={log.imageUrl} alt="" />}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {!hideSns && (profile.sns.instagram || profile.sns.tiktok) && (
        <div className={Styles.section}>
          <h5 className={Styles.sectionHeading}>SNS</h5>
          <div className={`${Styles.card} ${Styles.snsRow}`}>
            {profile.sns.instagram && (
              <span className={Styles.snsItem}><FaInstagram /> @{profile.sns.instagram}</span>
            )}
            {profile.sns.tiktok && (
              <span className={Styles.snsItem}><FaTiktok /> @{profile.sns.tiktok}</span>
            )}
          </div>
        </div>
      )}

      {hasSalonProfile && (
        <div className={Styles.section}>
          <h5 className={Styles.sectionHeading}>サロンへの要望</h5>
          <div className={Styles.card}>
            {salon.preferredAreas.length > 0 && (
              <div className={Styles.aboutMeItem}>
                <p className={Styles.aboutMeLabel}>希望勤務エリア</p>
                <p className={Styles.aboutMeText}>{salon.preferredAreas.join('、')}</p>
              </div>
            )}
            {salon.desiredJobType && (
              <div className={Styles.aboutMeItem}>
                <p className={Styles.aboutMeLabel}>希望職種</p>
                <p className={Styles.aboutMeText}>{salon.desiredJobType}</p>
              </div>
            )}
            {salon.desiredStartDate && (
              <div className={Styles.aboutMeItem}>
                <p className={Styles.aboutMeLabel}>入社希望時期</p>
                <p className={Styles.aboutMeText}>{salon.desiredStartDate}</p>
              </div>
            )}
            {salon.questionsForSalon.length > 0 && (
              <div className={Styles.aboutMeItem}>
                <p className={Styles.aboutMeLabel}>サロンへの質問</p>
                <p className={Styles.aboutMeText}>{salon.questionsForSalon.join('\n')}</p>
              </div>
            )}
            {salon.messageToSalon && (
              <div className={Styles.aboutMeItem}>
                <p className={Styles.aboutMeLabel}>サロンへのメッセージ</p>
                <p className={Styles.aboutMeText}>{salon.messageToSalon}</p>
              </div>
            )}
            {salon.contactMethod && (
              <div className={Styles.aboutMeItem}>
                <p className={Styles.aboutMeLabel}>希望連絡方法</p>
                <p className={Styles.aboutMeText}>{salon.contactMethod}</p>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default MyCardTab;
